import { useMemo, useCallback, type ReactNode } from "react";
import { Tag, Typography, Progress, Tooltip } from "antd";
import type { GridColumnMetadata } from "../types";
import {
  detectCurrencySymbol,
  formatCurrencyValue,
} from "../utils/currencyUtils";

const { Text } = Typography;

// Fields that typically contain long text content
const LONG_TEXT_FIELDS = [
  "notes",
  "description",
  "address",
  "bio",
  "remarks",
  "comments",
  "personalInfo",
  "personal_info",
  "ghi_chu",
  "mo_ta",
  "dia_chi",
];

// Max characters to show before truncating
const MAX_TEXT_LENGTH = 100;

/**
 * Check if a column is a long text field that should be truncated
 */
export const isLongTextField = (column: GridColumnMetadata): boolean => {
  const fieldName = column.name.toLowerCase();
  // Check by field name or data type
  return (
    LONG_TEXT_FIELDS.some((name) => fieldName.includes(name.toLowerCase())) ||
    column.dataType === "text" ||
    column.dataType === "ml_string" // Multi-line string in Aras
  );
};

type RenderFn = (value: unknown, record: Record<string, unknown>) => ReactNode;

// Color mappings for various fields
const priorityColors: Record<string, string> = {
  High: "red",
  Medium: "orange",
  Low: "green",
};

const statusColors: Record<string, string> = {
  // Lead/Opportunity statuses
  New: "blue",
  Contacted: "cyan",
  Qualified: "green",
  Negotiation: "orange",
  Won: "green",
  Lost: "red",
  // Customer statuses
  Active: "green",
  Inactive: "gray",
  Prospect: "blue",
  Churned: "red",
};

const stageColors: Record<string, string> = {
  Qualification: "blue",
  "Needs Analysis": "cyan",
  Proposal: "orange",
  Negotiation: "purple",
  "Closed Won": "green",
  "Closed Lost": "red",
};

const segmentColors: Record<string, string> = {
  Enterprise: "purple",
  SMB: "blue",
  Startup: "cyan",
  Individual: "green",
};

const industryColors: Record<string, string> = {
  Technology: "blue",
  Education: "cyan",
  Healthcare: "green",
  Finance: "gold",
  Retail: "orange",
  Manufacturing: "purple",
};

/**
 * Wrapper to add tooltip to any text content for ellipsis support
 */
const withTooltip = (content: ReactNode, tooltipText?: string): ReactNode => {
  if (!tooltipText) return content;
  return (
    <Tooltip
      title={tooltipText}
      placement="topLeft"
      overlayStyle={{ maxWidth: 400 }}
    >
      <span>{content}</span>
    </Tooltip>
  );
};

// Default renderers by data type - all text content wrapped with tooltip
const defaultRenderers: Record<string, (value: unknown) => ReactNode> = {
  string: (value) => {
    if (!value) return <Text type="secondary">-</Text>;
    const text = String(value);
    return withTooltip(<Text>{text}</Text>, text);
  },
  text: (value) => {
    if (!value) return <Text type="secondary">-</Text>;
    const text = String(value);
    return withTooltip(<Text>{text}</Text>, text);
  },
  integer: (value) =>
    value !== undefined && value !== null ? (
      Number(value).toLocaleString()
    ) : (
      <Text type="secondary">-</Text>
    ),
  float: (value) =>
    value !== undefined && value !== null ? (
      Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })
    ) : (
      <Text type="secondary">-</Text>
    ),
  decimal: (value) =>
    value !== undefined && value !== null ? (
      Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })
    ) : (
      <Text type="secondary">-</Text>
    ),
  date: (value) =>
    value ? (
      new Date(value as string).toLocaleDateString()
    ) : (
      <Text type="secondary">-</Text>
    ),
  time: (value) => {
    if (!value) return <Text type="secondary">-</Text>;
    // Handle ISO datetime string (e.g., "2026-02-02T00:53:00") or time string
    const strValue = String(value);
    try {
      // Try to parse as ISO datetime and extract time portion
      if (strValue.includes("T")) {
        const timePart = strValue.split("T")[1];
        // Extract HH:mm from HH:mm:ss
        const [hours, minutes] = timePart.split(":");
        return `${hours}:${minutes}`;
      }
      // If already in time format (HH:mm:ss or HH:mm), just return HH:mm
      const [hours, minutes] = strValue.split(":");
      return `${hours}:${minutes}`;
    } catch {
      return strValue;
    }
  },
  boolean: (value) => (value ? "Yes" : "No"),
  list: (value) =>
    value ? <Tag>{String(value)}</Tag> : <Text type="secondary">-</Text>,
  // "filter list" uses same renderer as "list" (both are single-select in Aras)
  "filter list": (value) =>
    value ? <Tag>{String(value)}</Tag> : <Text type="secondary">-</Text>,
  item: (value) => {
    if (!value) return <Text type="secondary">-</Text>;
    const text = String(value);
    return withTooltip(<Text>{text}</Text>, text);
  },
};

// Custom field-specific renderers (override defaults)
const fieldRenderers: Record<string, RenderFn> = {
  // ID field
  itemNumber: (value) => {
    if (!value) return <Text type="secondary">-</Text>;
    const text = String(value);
    return withTooltip(<Text code>{text}</Text>, text);
  },

  // Priority field
  priority: (value) =>
    value ? (
      <Tag color={priorityColors[value as string] || "default"}>
        {value as string}
      </Tag>
    ) : (
      <Text type="secondary">-</Text>
    ),

  // Status field (Lead/Customer)
  status: (value) =>
    value ? (
      <Tag color={statusColors[value as string] || "default"}>
        {value as string}
      </Tag>
    ) : (
      <Text type="secondary">-</Text>
    ),

  // Stage field (Opportunity)
  stage: (value) =>
    value ? (
      <Tag color={stageColors[value as string] || "default"}>
        {value as string}
      </Tag>
    ) : (
      <Text type="secondary">-</Text>
    ),

  // Segment field (Customer)
  segment: (value) =>
    value ? (
      <Tag color={segmentColors[value as string] || "default"}>
        {value as string}
      </Tag>
    ) : (
      <Text type="secondary">-</Text>
    ),

  // Industry field (Organization)
  industry: (value) =>
    value ? (
      <Tag color={industryColors[value as string] || "default"}>
        {value as string}
      </Tag>
    ) : (
      <Text type="secondary">-</Text>
    ),

  // Probability field (Opportunity) - Progress bar
  probability: (value) =>
    value !== undefined && value !== null ? (
      <Progress percent={value as number} size="small" />
    ) : (
      <Text type="secondary">-</Text>
    ),

  // (Currency rendering is handled generically via detectCurrencySymbol in
  // getRenderer below — driven by label suffix like "(VND)" or money-ish
  // field-name suffixes — so individual entries are no longer needed.)

  // Working time field (ERP_Attendance) - format ISO datetime to HH:mm
  workingTime: (value) => {
    if (!value) return <Text type="secondary">-</Text>;
    const strValue = String(value);
    try {
      // Handle ISO datetime string (e.g., "2026-02-02T00:53:00")
      if (strValue.includes("T")) {
        const timePart = strValue.split("T")[1];
        const [hours, minutes] = timePart.split(":");
        return `${hours}:${minutes}`;
      }
      // If already in time format (HH:mm:ss or HH:mm), just return HH:mm
      const [hours, minutes] = strValue.split(":");
      return `${hours}:${minutes}`;
    } catch {
      return strValue;
    }
  },

  // Overtime field (ERP_Attendance) - format ISO datetime to HH:mm
  overtime: (value) => {
    if (!value) return <Text type="secondary">-</Text>;
    const strValue = String(value);
    try {
      // Handle ISO datetime string (e.g., "2026-02-02T00:53:00")
      if (strValue.includes("T")) {
        const timePart = strValue.split("T")[1];
        const [hours, minutes] = timePart.split(":");
        return `${hours}:${minutes}`;
      }
      // If already in time format (HH:mm:ss or HH:mm), just return HH:mm
      const [hours, minutes] = strValue.split(":");
      return `${hours}:${minutes}`;
    } catch {
      return strValue;
    }
  },

  // Lifecycle state field
  state: (value, record) => {
    const text = (record.stateLabel as string) || (value as string) || "Draft";
    return withTooltip(<Tag>{text}</Tag>, text);
  },

  // Employee count
  employeeCount: (value) => {
    if (value === undefined || value === null) {
      return <Text type="secondary">-</Text>;
    }
    const text = Number(value).toLocaleString();
    return withTooltip(<Text>{text}</Text>, text);
  },
};

/**
 * Renderer for long text fields with tooltip
 */
const longTextRenderer: RenderFn = (value) => {
  if (!value) {
    return <Text type="secondary">-</Text>;
  }

  const text = String(value);

  // If text is short enough, no need for tooltip
  if (text.length <= MAX_TEXT_LENGTH) {
    return <Text>{text}</Text>;
  }

  // Show truncated text with tooltip for full content
  return (
    <Tooltip title={text} placement="topLeft" overlayStyle={{ maxWidth: 400 }}>
      <Text>{text}</Text>
    </Tooltip>
  );
};

// Build a renderer that formats a number as currency, preferring the row's
// `currency` column if present (multi-currency records), otherwise the
// detected symbol from label/name.
const buildCurrencyRenderer = (defaultSymbol: string): RenderFn => {
  return (value, record) => {
    if (value === undefined || value === null || value === "") {
      return <Text type="secondary">-</Text>;
    }
    const symbol = (record.currency as string) || defaultSymbol;
    const text = formatCurrencyValue(value as number, symbol);
    return withTooltip(<Text>{text}</Text>, text);
  };
};

export const useColumnRenderers = () => {
  const getRenderer = useCallback((column: GridColumnMetadata): RenderFn => {
    // Check for field-specific renderer first
    if (fieldRenderers[column.name]) {
      return fieldRenderers[column.name];
    }

    // Currency detection — applies to numeric columns whose label or name
    // signals money (e.g. "Học phí (VND)", "amount", "monthly_fee").
    const numericTypes = ["integer", "decimal", "float"];
    if (numericTypes.includes(column.dataType)) {
      const symbol = detectCurrencySymbol({
        label: column.label,
        mappedName: column.name,
      });
      if (symbol) return buildCurrencyRenderer(symbol);
    }

    // Use long text renderer for fields that can have long content
    if (isLongTextField(column)) {
      return longTextRenderer;
    }

    // Fall back to data type renderer
    const typeRenderer =
      defaultRenderers[column.dataType] || defaultRenderers.string;
    return (value) => typeRenderer(value);
  }, []);

  const renderers = useMemo(
    () => ({
      getRenderer,
      fieldRenderers,
      defaultRenderers,
    }),
    [getRenderer],
  );

  return renderers;
};
